import React, { useRef, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useConfig, useCountryData, useCookie } from 'msp-integrations'
import {
  AlertBox,
  transformRegionToMap,
  DynamicTag,
  Icons,
  Button,
  Field,
  getPrefixPhone
} from '../../../index'
import getContactFields from './ContactForm.fields'
import { ContactFormProps } from './ContactForm.types'
import { ContactFormStyles } from './ContactForm.styles'

/*
  Hint: To run this component locally, please use 127.0.0.1 instead of "localhost" because service cloud doesn't send back response when using localhost.
 */
const ContactForm = ({ data, regions = [], onSubmit }: ContactFormProps) => {
  const formRef = useRef(null)
  const { eventTracking } = useCookie()
  const { formatMessage } = useIntl()
  const {
    phonePREFIX,
    ssr: { serviceCloudKeys }
  } = useConfig()
  const i18n = useCountryData()
  const countryCode = i18n.locale?.split('_')[1]
  const fieldConfig = getContactFields()
  const form = useForm()
  const alertBoxRef = useRef<typeof AlertBox>()
  const [returnUrl, setReturnUrl] = useState('')
  const [originUrl, setOriginUrl] = useState('')
  const [prefix, setPrefix] = useState(phonePREFIX)

  const formHeadlineData =
    transformRegionToMap(regions)?.get('formHeadline')?.components || []

  const submitForm = () => {
    const fullPhone = `+${getPrefixPhone(prefix)} ${form.getValues('phone')}`
    form.setValue('phone', fullPhone)

    sessionStorage.setItem('contactForm', 'success')
    // @ts-ignore
    formRef.current.submit()
    form.reset()
  }

  useEffect(() => {
    const success = sessionStorage.getItem('contactForm') === 'success'
    if (success) {
      sessionStorage.removeItem('contactForm')
      eventTracking('formsSubmit', { gtm: { elementId: 'contact-form' } })
      //show the alert box
      alertBoxRef?.current?.showAlert()
      onSubmit && onSubmit()
    }
  }, [onSubmit])

  useEffect(() => {
    setReturnUrl(window.location.href)
    setOriginUrl(`${window.location.origin}${window.location.pathname}`)
  }, [])

  return (
    <div
      className={ContactFormStyles.wrapperStyles}
      id={data?.wrapperClassName || 'contact-form'}
    >
      <AlertBox
        ref={alertBoxRef}
        type='success'
        position='top'
        autoCloseTime={10000}
        title={formatMessage({
          defaultMessage: 'Thanks for contacting us.',
          id: 'msp.contact_form.msg.thanks'
        })}
        description={formatMessage({
          defaultMessage: 'Your message was sent successfully.',
          id: 'msp.contact_form.msg.success'
        })}
        closeButtonIcon={
          <div className={ContactFormStyles.fillWhite}>
            <Icons iconName='Close' size='base' />
          </div>
        }
      />
      {formHeadlineData[0].data?.children && (
        <div className={ContactFormStyles.headlineWrapper}>
          <DynamicTag
            componentTagName='Headline'
            colorScheme='whiteAlpha'
            tag='h3'
          >
            {formHeadlineData[0].data?.children}
          </DynamicTag>
        </div>
      )}
      <form
        action={serviceCloudKeys?.contactFormSubmitURL}
        method='POST'
        ref={formRef}
        onSubmit={form.handleSubmit(submitForm)}
        data-testid={'contact-form'}
      >
        <input
          type='hidden'
          id='orgid'
          name='orgid'
          value={serviceCloudKeys?.contactFormKey}
        />
        <input
          type='hidden'
          id='Web_Origin_URL__c'
          name='Web_Origin_URL__c'
          value={originUrl}
        />
        <input type='hidden' id='retURL' name='retURL' value={returnUrl} />
        <input
          type='hidden'
          id='00N68000000NutT'
          name='00N68000000NutT'
          value={countryCode}
        />
        <div className={ContactFormStyles.commonWrapperStyle}>
          <Field
            control={form.control}
            config={fieldConfig.name}
            error={form.errors?.name}
          />
        </div>
        <div className={ContactFormStyles.emailPhoneWrapper}>
          <div className={ContactFormStyles.emailWrapper}>
            <Field
              control={form.control}
              config={fieldConfig.email}
              error={form.errors?.email}
            />
          </div>
          <div className={ContactFormStyles.phoneWrapper}>
            <Field
              control={form.control}
              config={{
                ...fieldConfig.phone,
                onUpdatePrefix: (value) => setPrefix(value),
                initialPrefix: phonePREFIX
              }}
            />
          </div>
        </div>
        <div className={ContactFormStyles.commonWrapperStyle}>
          <Field
            className={ContactFormStyles.selectBoxStyle}
            control={form.control}
            config={fieldConfig.Contact_Reason}
            error={form.errors?.Contact_Reason}
          />
        </div>
        <div className={ContactFormStyles.commonWrapperStyle}>
          <Field
            control={form.control}
            config={fieldConfig.description}
            error={form.errors?.description}
          />
        </div>
        <div className={ContactFormStyles.commonWrapperStyle}>
          <Field
            control={form.control}
            config={fieldConfig.captcha}
            error={form.errors?.captcha}
          />
        </div>
        <div className={ContactFormStyles.buttonWrapper}>
          <Button
            onClick={() =>
              form.clearErrors([
                'name',
                'email',
                'Contact_Reason',
                'contactPhone',
                'description'
              ])
            }
            isDisabled={form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
            isSubmit
          >
            {formatMessage({
              defaultMessage: 'Send',
              id: 'msp.contact_form.button.send'
            })}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default ContactForm
